// The Workspace sidebar: the workspace's name (its menu), my own things (the
// Inbox, mine and the workspace's together, and Me's Cases, Agents, Documents
// and People), a line, the workspace's own sections, the favorites kept in
// this workspace, and at the foot what goes on (the call one is in). The same
// rows as Me's sidebar.
//
// Me's rows open Me's pages inside this workspace (me-place.ts): this sidebar
// stays around them and Me is narrowed to the workspace's label.
import { useQuery } from "@tanstack/react-query";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { workspacePlace } from "@trameria/cloud-api/workspace-shared";
import { m } from "@trameria/cloud-i18n";
import { m as common } from "@trameria/i18n";
import {
  CollapsedColumn,
  FavoritesSection,
  Icon,
  IconDrawerRow,
  IconGroupGap,
  IconNavRow,
  type MenuItem,
  type NavItem,
  NavRow,
  OngoingRows,
  SidebarSwitcher,
  useApi,
  useMeApp,
} from "@trameria/ui-core";
import { useCan, useWorkspace, useWorkspaceHost } from "./context";
import { MY_THINGS_PATHS } from "./me-place";
import { spacesQuery } from "./queries";
import { sectionShown, type WorkspaceSection } from "./sections";

/** The sections shown, split into my own things' and the workspace's. */
function useSections() {
  const { slug, view } = useWorkspace();
  const host = useWorkspaceHost();
  const spaces = useQuery(spacesQuery(useApi(), slug)).data ?? [];
  const shown = host.sections.filter((s) => sectionShown(s, view, spaces));
  return {
    mine: shown.filter((s) => s.mine),
    theirs: shown.filter((s) => !s.mine),
  };
}

/** Me's nav items the sidebar has rows for, in Me's order. */
function useMyThings(): NavItem[] {
  const paths: readonly string[] = MY_THINGS_PATHS;
  return useMeApp().nav.filter((n) => paths.includes(n.to));
}

export function WorkspaceSidebar() {
  const { slug, view } = useWorkspace();
  const { mine, theirs } = useSections();
  const things = useMyThings();
  return (
    <nav
      aria-label={view.name}
      className="flex h-full min-h-0 flex-col bg-sidebar pt-2.5"
    >
      <div className="px-2">
        <WorkspaceMenu />
      </div>
      <div className="mt-2 min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        <div className="flex flex-col gap-px">
          {mine.map((s) => (
            <SectionRow key={s.id} slug={slug} section={s} />
          ))}
          {things.map((n) => (
            <MyThingRow key={n.to} item={n} />
          ))}
        </div>
        <Rule />
        <div className="flex flex-col gap-px">
          {theirs.map((s) => (
            <SectionRow key={s.id} slug={slug} section={s} />
          ))}
        </div>
        <FavoritesSection place={workspacePlace(view.id)} />
      </div>
      <OngoingRows shape="row" className="px-2 pb-2.5" />
    </nav>
  );
}

/**
 * The same sidebar folded to its icons: my own things, a line, the
 * workspace's sections, then the favorites behind one button (they open the
 * full sidebar over the panel). At the top, the workspace's menu behind its
 * tile, where the full sidebar has its switcher.
 */
export function WorkspaceSidebarCollapsed() {
  const { slug, view } = useWorkspace();
  const { mine, theirs } = useSections();
  const things = useMyThings();
  return (
    <CollapsedColumn label={view.name} head={<WorkspaceMenu folded />}>
      {mine.map((s) => (
        <SectionIcon key={s.id} slug={slug} section={s} />
      ))}
      {things.map((n) => (
        <MyThingIcon key={n.to} item={n} />
      ))}
      <Rule folded />
      {theirs.map((s) => (
        <SectionIcon key={s.id} slug={slug} section={s} />
      ))}
      <IconGroupGap />
      <IconDrawerRow
        icon={<Icon kind="favorite" className="pointer-events-none" />}
        label={common.shell_favorites()}
      />
    </CollapsedColumn>
  );
}

/**
 * The line between my own things and the workspace's: 8 px above and below
 * it in both, so the rows under it stay put as the sidebar grows out of the
 * column. The folded column puts 1 px between all its rows, the line too, so
 * its margins are 1 px less there.
 */
function Rule({ folded = false }: { folded?: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={`h-px flex-none bg-border ${folded ? "my-[7px] w-4" : "mx-2.5 my-2"}`}
    />
  );
}

function MyThingRow({ item }: { item: NavItem }) {
  const badge = item.useBadge?.();
  return (
    <NavRow to={item.to} icon={item.icon} label={item.label()} end={badge} />
  );
}

function MyThingIcon({ item }: { item: NavItem }) {
  const mark = item.useBadge?.();
  return (
    <IconNavRow
      to={item.to}
      icon={item.icon}
      label={item.label()}
      mark={mark}
    />
  );
}

/**
 * Whether a section that owns more paths than its own is the current place
 * (undefined for one that owns only its own: its link tells by itself).
 */
function useSectionCurrent(
  slug: string,
  section: WorkspaceSection,
): boolean | undefined {
  const matchRoute = useMatchRoute();
  if (!section.paths?.length) return undefined;
  return [section.to, ...section.paths].some(
    (p) => matchRoute({ to: `/${slug}${p}`, fuzzy: true }) !== false,
  );
}

function SectionIcon({
  slug,
  section,
}: {
  slug: string;
  section: WorkspaceSection;
}) {
  const mark = section.useBadge?.();
  return (
    <IconNavRow
      to={`/${slug}${section.to}`}
      icon={section.icon}
      label={section.label()}
      mark={mark}
      current={useSectionCurrent(slug, section)}
    />
  );
}

function SectionRow({
  slug,
  section,
}: {
  slug: string;
  section: WorkspaceSection;
}) {
  const badge = section.useBadge?.();
  return (
    <NavRow
      to={`/${slug}${section.to}`}
      icon={section.icon}
      label={section.label()}
      end={badge}
      current={useSectionCurrent(slug, section)}
    />
  );
}

function WorkspaceMenu({ folded = false }: { folded?: boolean }) {
  const { slug, view } = useWorkspace();
  const can = useCan();
  const navigate = useNavigate();
  const items: MenuItem[] = [
    {
      id: "settings",
      label: m.ws_menu_settings(),
      icon: "settings",
      onSelect: () => void navigate({ to: `/${slug}/settings` }),
    },
    ...(can("workspace.invite")
      ? [
          {
            id: "invite",
            label: m.ws_menu_invite(),
            icon: "invite" as const,
            onSelect: () =>
              void navigate({ to: `/${slug}/settings/invites/new` }),
          },
        ]
      : []),
    {
      id: "new",
      label: m.ws_menu_new(),
      icon: "plus",
      onSelect: () => void navigate({ to: "/new" }),
    },
  ];
  return (
    <SidebarSwitcher
      folded={folded}
      label={m.ws_menu({ name: view.name })}
      // A workspace's own mark, not its tile: the rail beside shows the tile
      // already (the person, 2026-10-30).
      mark={
        <span className="flex size-[22px] flex-none items-center justify-center text-sidebar-muted">
          <Icon kind="workspace" size={18} />
        </span>
      }
      name={view.name}
      items={items}
    />
  );
}
